import React, { useMemo } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { HighchartsTooltipElementProps } from '@components-clean/core/charts/combo-column-line-chart';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import PanelFrame from '@components-clean/core/panel-frame';
import { StudyDashboardPanelData } from '@interfaces/central-monitoring';
import { PanelType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

interface IProps {
    panel: StudyDashboardPanelData;
    showLegend?: boolean;
    hidePanelSetup?: boolean;
}

const SeriesName = {
    RED: 'Red Sites',
    AMBER: 'Amber Sites',
    GREEN: 'Green Sites',
    PURPLE: 'Purple Sites',
    STUDY_MEAN: 'Study Site Mean',
    COUNTRY_MEAN: 'Country Site Mean',
    COUNTRY_RATE: 'Country Rate',
    SITE_RATE: 'Site Rate',
    SITE_SCORE: 'Site Score',
    STUDY_RATE: 'Study Rate',
};

const Tooltip = ({ context }: HighchartsTooltipElementProps) => {
    let data: (string | number | null | undefined)[][];

    switch (context.series.name) {
        case SeriesName.RED:
        case SeriesName.AMBER:
        case SeriesName.GREEN:
        case SeriesName.PURPLE:
            data = [
                ['# of Sites', context.y],
                ['Percentage', `${bankersRound(context.percentage, 2)}%`],
                ['Color', context.series.name],
                ['Dataset', context.key],
            ];
            break;
        case SeriesName.STUDY_MEAN:
        case SeriesName.COUNTRY_MEAN:
        case SeriesName.COUNTRY_RATE:
        case SeriesName.SITE_RATE:
        case SeriesName.SITE_SCORE:
        case SeriesName.STUDY_RATE:
            data = [
                [context.series.name, context.y ?? 0],
                ['Dataset', context.key],
            ];
            break;
        default:
            data = [];
    }
    return (
        <table>
            {data.map((info) => (
                <tr key={info[0]}>
                    <td>
                        {info[0]}: {info[1]}
                    </td>
                </tr>
            ))}
        </table>
    );
};

const ChartPanel = ({ panel, showLegend = false, hidePanelSetup = true }: IProps) => {
    const { t } = useTranslation('cm');
    const { reference, plotTitle, datasets, lineToShow, includePurpleSites } = panel;

    const series = useMemo(() => {
        if (datasets.length === 0) return [];

        const result: SeriesOptionsType[] = [
            {
                name: SeriesName.RED,
                type: 'column',
                data: datasets.map((dataset) => dataset.redCount),
                custom: {
                    ragColorIndex: 1,
                },
            },
            {
                name: SeriesName.AMBER,
                type: 'column',
                data: datasets.map((dataset) => dataset.amberCount),
                custom: {
                    ragColorIndex: 2,
                },
            },
            {
                name: SeriesName.GREEN,
                type: 'column',
                data: datasets.map((dataset) => dataset.greenCount),
                custom: {
                    ragColorIndex: 3,
                },
            },
        ];

        if (includePurpleSites) {
            result.push({
                name: SeriesName.PURPLE,
                type: 'column',
                data: datasets.map((dataset) => dataset.purpleCount ?? null),
                custom: {
                    ragColorIndex: 4,
                },
            });
        }

        switch (lineToShow) {
            case 'StudyMean':
                result.push({
                    name: SeriesName.STUDY_MEAN,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.studyMean ?? null),
                    yAxis: 1,
                });
                break;
            case 'CountryMean':
                result.push({
                    name: SeriesName.COUNTRY_MEAN,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.countryMean ?? null),
                    yAxis: 1,
                });
                break;
            case 'SiteRate':
                result.push({
                    name: SeriesName.SITE_RATE,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.siteRate ?? null),
                    yAxis: 1,
                });
                break;
            case 'CountryRate':
                result.push({
                    name: SeriesName.COUNTRY_RATE,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.countryRate ?? null),
                    yAxis: 1,
                });
                break;
            case 'SiteScore':
                result.push({
                    name: SeriesName.SITE_SCORE,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.siteRate ?? null),
                    yAxis: 1,
                });
                break;
            case 'StudyRate':
                result.push({
                    name: SeriesName.STUDY_RATE,
                    type: 'line',
                    data: datasets.map((dataset) => dataset.studyRate ?? null),
                    yAxis: 1,
                });
                break;
            default:
                break;
        }

        return result;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panel]);

    return (
        <PanelFrame
            panelType={PanelType.GRAPH}
            graphType="COMBOCOLUMNLINE"
            panelData={{
                plotTitle,
                series,
                xCategories: datasets.map((dataset) => stringDateWithoutTimeFromUtc(dataset.date, 'DD-MMM-YYYY')!),
                yTitle: `${t('studyHubPage.numberOfSites')}`,
                yTitleSecondary: lineToShow ? (lineToShow === 'SiteScore' ? 'Site Score' : 'Rate/Mean') : undefined,
            }}
            title={plotTitle}
            data-testid={`panel-${reference}`}
            showLegend={showLegend}
            TooltipElement={Tooltip}
            hidePanelSetup={hidePanelSetup}
            dataviewRef={panel.dataviewRef}
        />
    );
};

export default ChartPanel;
